// src/components/MessageModal.jsx
import React, { useState } from 'react';
import { fillTemplate, getTemplateForTier, SMS_TEMPLATE_KEYS, TEMPLATE_LABELS } from '../utils/messageTemplates';
import { getSmsBlockReason } from '../utils/compliance';

export default function MessageModal({ tenant, initialMode, propertyName, onClose, onSend }) {
  const tier = tenant?.risk?.tier || 'low';
  const outreachCount = (tenant?.outreachLog || []).length;
  const defaultLang = tenant?.preferredLanguage || 'en';
  const smsBlockReason = getSmsBlockReason(tenant);

  // If mode is 'plan', pre-select paymentPlan template
  const defaultKey = initialMode === 'plan' ? 'paymentPlan' : getTemplateForTier(tier, outreachCount);
  const [selectedTemplate, setSelectedTemplate] = useState(defaultKey);
  const [lang, setLang] = useState(defaultLang);
  const [message, setMessage] = useState(() => fillTemplate(defaultKey, tenant, defaultLang, { property: propertyName }));
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const handleTemplateChange = (key) => {
    setSelectedTemplate(key);
    setMessage(fillTemplate(key, tenant, lang, { property: propertyName }));
  };

  const handleLangChange = (l) => {
    setLang(l);
    setMessage(fillTemplate(selectedTemplate, tenant, l, { property: propertyName }));
  };

  const handleSend = async () => {
    if (sending || !message.trim()) return;
    setSending(true);
    try {
      await onSend(tenant, message, selectedTemplate, lang);
      setSent(true);
      setTimeout(() => onClose(), 1500);
    } catch (err) {
      alert('Failed to send: ' + err.message);
      setSending(false);
    }
  };

  if (!tenant) return null;

  // Suggested installment amounts for payment plan
  const balance = tenant.currentBalance || 0;
  const inst2 = (balance / 2).toFixed(2);
  const inst3 = (balance / 3).toFixed(2);

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center sm:p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-t-2xl sm:rounded-2xl shadow-xl w-full sm:max-w-lg max-h-[92dvh] overflow-y-auto safe-bottom">
        {/* Header */}
        <div className="p-5 border-b border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="font-semibold text-gray-900">
                Send Message to {tenant.name}
              </h2>
              <p className="text-sm text-gray-500 mt-0.5">
                Unit {tenant.unit} · {tenant.phone || 'No phone on file'}
              </p>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none">✕</button>
          </div>
        </div>

        {sent ? (
          <div className="p-8 text-center">
            <div className="text-4xl mb-3">✅</div>
            <p className="font-semibold text-green-700">Message sent to {tenant.name}!</p>
            <p className="text-sm text-gray-500 mt-1">Logged to outreach history.</p>
          </div>
        ) : (
          <div className="p-5 space-y-4">
            {/* Template selector */}
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Message Template</label>
              <div className="flex flex-wrap gap-2">
                {SMS_TEMPLATE_KEYS.map(key => (
                  <button key={key} onClick={() => handleTemplateChange(key)}
                    className={"px-3 py-1.5 rounded-lg text-sm font-medium transition-colors " +
                      (selectedTemplate === key ? 'bg-tenafi-green text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200')}>
                    {TEMPLATE_LABELS[key]}
                  </button>
                ))}
              </div>
            </div>

            {/* Language toggle */}
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Language</label>
              <div className="flex gap-2">
                <button onClick={() => handleLangChange('en')}
                  className={"px-4 py-1.5 rounded-lg text-sm font-medium transition-colors " +
                    (lang === 'en' ? 'bg-tenafi-green text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200')}>
                  🇺🇸 English
                </button>
                <button onClick={() => handleLangChange('es')}
                  className={"px-4 py-1.5 rounded-lg text-sm font-medium transition-colors " +
                    (lang === 'es' ? 'bg-tenafi-green text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200')}>
                  🇲🇽 Español
                </button>
              </div>
            </div>

            {/* Payment plan suggestion */}
            {selectedTemplate === 'paymentPlan' && balance > 0 && (
              <div className="bg-tenafi-light border border-tenafi-green/20 rounded-xl p-3 text-sm text-tenafi-green-dark">
                <span className="font-semibold">Suggested splits: </span>
                2 payments of $${inst2} · or 3 payments of $${inst3}
              </div>
            )}

            {/* Editable message */}
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Message</label>
              <textarea value={message} onChange={e => setMessage(e.target.value)} rows={5}
                className="w-full border border-gray-300 rounded-xl p-3 text-sm text-gray-700 focus:ring-2 focus:ring-tenafi-green outline-none resize-none" />
              <p className="text-xs text-gray-400 mt-1">{message.length} characters</p>
              <p className="text-xs text-gray-400 mt-1">STOP/ALTO opt-out text and a secure payment link are appended automatically when needed.</p>
            </div>

            {/* SMS preview bubble */}
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Preview</label>
              <div className="bg-gray-100 rounded-2xl p-3 max-w-xs">
                <div className="bg-gray-300 rounded-2xl rounded-bl-sm px-3 py-2 text-sm text-gray-800 leading-snug">
                  {message}
                </div>
              </div>
            </div>

            {smsBlockReason && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-sm text-amber-800">
                {smsBlockReason}
              </div>
            )}
          </div>
        )}

        {!sent && (
          <div className="p-5 border-t border-gray-100 flex items-center justify-end gap-3">
            <button onClick={onClose} className="px-4 py-2.5 text-sm font-medium text-gray-600 hover:text-gray-800">Cancel</button>
            <button onClick={handleSend} disabled={sending || !message.trim() || !tenant.phone || !!smsBlockReason}
              className="px-5 py-2.5 bg-tenafi-green text-white rounded-xl text-sm font-semibold hover:bg-tenafi-green-dark transition-colors disabled:opacity-50">
              {sending ? 'Sending...' : 'Send SMS'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
